import React, { useState } from 'react';
import type { RestaurantFormData } from '../restaurant.types';
import { BsStars } from "react-icons/bs";
import { AIText } from '../../../lib/api/restaurant.apis';
import { RotatingLines } from 'react-loader-spinner';

interface BasicInfoStepProps {
  formData: RestaurantFormData;
  updateFormData: (data: Partial<RestaurantFormData>) => void;
  isActive: boolean;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  formData,
  updateFormData,
  isActive,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [nameError, setNameError] = useState('');

  const handleChange = (field: keyof RestaurantFormData, value: any) => {
    updateFormData({ [field]: value });
    // Clear name error when user starts typing
    if (field === 'name' && nameError) {
      setNameError('');
    }
  };

  const handleDescriptionChange = (value: string) => {
    if (value.length <= 500) {
      handleChange('description', value);
    }
  };

  const validateName = (): boolean => {
    if (!formData.name || formData.name.trim().length < 6) {
      setNameError('Name is required and must be at least 6 characters');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleAIGenerate = async () => {
    if (isGenerating) return;
    
    // Validate name before making AI request
    if (!validateName()) {
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const response = await AIText({
        type: "description",
        name: formData.name,
        cuisine: formData.cuisine,
        typeOfFood: formData.typeOfFood,
        experience: formData.experience,
        tags: formData.tags,
      });

      if (response.text) {
        handleChange('description', response.text);
      }
    } catch (error) {
      console.error('Failed to generate AI description:', error);
      // You might want to show a toast or error message here
    } finally {
      setIsGenerating(false);
    }
  };

  return ( 
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={isActive ? 'fade-in stagger-1' : 'opacity-0'}>
          <label htmlFor="name" className="block text-sm font-medium text-[#111827] mb-2">
            Restaurant Name *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all ${
              nameError ? 'border-red-500' : 'border-[#FFDDA6]'
            }`}
            placeholder="Enter your gem name"
            required
          />
          {nameError && (
            <p className="mt-1 text-sm text-red-500">{nameError}</p>
          )}
        </div>
      </div>

      <div className={isActive ? 'fade-in stagger-3' : 'opacity-0'}>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="description" className="block text-sm font-medium text-[#111827]">
            Description *
          </label>

          {/* AI Generate Button */}
          <button
            type="button"
            onClick={handleAIGenerate}
            disabled={isGenerating}
            className="flex items-center gap-1 text-red-500 hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            title="Generate description with AI"
          >
            {isGenerating ? (
              <RotatingLines 
                height="24"
                width={"24"}
                color='#EF4444'
                animationDuration={"1.4"}
                visible={true}
                ariaLabel="rotating-lines-loading"
                strokeWidth={"3"}              
              />
            ) : (
              <BsStars size={24} className="text-base" />
            )}
          </button>
        </div>

        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          rows={4}
          className="w-full px-4 py-3 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all resize-vertical disabled:opacity-50 disabled:cursor-not-allowed"
          placeholder={isGenerating ? "Writing..." : "Describe your restaurant (max 500 characters)"}
          maxLength={700}
          minLength={200}
          required
          disabled={isGenerating}
        />

        <div className="flex justify-between text-sm text-[#5E503F] mt-2">
          <span>Brief description of your restaurant</span>
          <span>{formData.description.length}/700</span>
        </div>
      </div>

      {/* CONTACT INFO */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Information</h3>

        <div className="space-y-3">

          {/* PHONE */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="text"
              value={formData.contactInfo?.phone}
              onChange={(e) =>
                updateFormData({
                  contactInfo: {
                    ...formData.contactInfo,
                    phone: e.target.value,
                  },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent"
              placeholder="Enter phone number"
            />
          </div>

          {/* EMAIL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.contactInfo?.email}
              onChange={(e) =>
                updateFormData({
                  contactInfo: {
                    ...formData?.contactInfo,
                    email: e.target.value,
                  },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent"
              placeholder="Enter email"
            />
          </div>

          {/* WEBSITE */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="text"
              value={formData?.contactInfo?.website}
              onChange={(e) =>
                updateFormData({
                  contactInfo: {
                    ...formData.contactInfo,
                    website: e.target.value,
                  },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;